<stb-modal
  [isShowing]="true"
  [responsiveMode]="'normal'"
  [header]="'Dev panel'"
  [primaryText]="''"
  [cancelText]="'Lukk'"
  (cancelClick)="close()"
>
  <div class="flex min-h-0 grow flex-col">
    <div
      data-testid="dev-filter-bar"
      class="flex shrink-0 items-center gap-08 border-b border-[var(--p-color-stroke-subtle-2)] p-12"
    >
      <stb-form [rebrand]="true" size="fluid" class="min-w-0 grow">
        <input
          #filterInput
          type="text"
          placeholder="Filter…"
          aria-label="Filter dev panel"
          [value]="filter.query()"
          (input)="onFilterInput(filterInput.value)"
          (keydown)="onFilterKeydown($event)"
        />
      </stb-form>
      @if (filter.query() !== "") {
        <button
          stbButtonCircle
          iconName="close"
          label="Clear filter"
          size="small"
          class="shrink-0"
          (click)="filter.clear(); filterInput.focus()"
        ></button>
      }
      <!-- aria-live because typing in the filter changes this number and nothing else
       announces the result: without it a screen-reader user gets no feedback that the
       query narrowed anything, and no warning that the active section was retargeted. -->
      <span data-testid="dev-row-count" class="font-details shrink-0 whitespace-nowrap" aria-live="polite"
        >{{ visibleRowCount() }} / {{ totalRowCount() }}</span
      >
    </div>

    <div class="flex min-h-0 grow">
      <nav class="flex w-[200px] shrink-0 flex-col border-r border-[var(--p-color-stroke-subtle-2)]">
        <div
          role="tablist"
          aria-orientation="vertical"
          aria-label="Dev panel sections"
          class="flex min-h-0 grow flex-col gap-04 overflow-y-auto p-08"
        >
          <!-- Tabs are never disabled, however few rows match. `disabled` here was an
           accessibility trap: a query matching nothing disabled all five tabs at once,
           and because the roving tabindex still put tabindex="0" on the active one, the
           whole tablist dropped out of the tab order with no keyboard or pointer route
           back. An empty section states that it is empty instead. -->
          <!-- Styled from the design system's secondary-button tokens rather than with
           stbButton itself, which cannot be used here for three reasons:
           StbButtonDirective host-binds attr.tabindex to a hardcoded 0 with no input to
           override it, which would put all five tabs in the tab order and undo the
           roving tabindex above; button[stbButton] is a component whose ng-content
           wrapper centres its children and forbids wrapping, so the label-left /
           count-right layout is not expressible; and its ViewEncapsulation.None styles
           fix the height and force a pill radius. Only the active tab draws the
           outline. The border is transparent rather than absent on the others so
           selecting one does not shift the label by a pixel — but note that
           border-transparent is *bound*, not static: two border-colour utilities on one
           element have the same specificity, so stylesheet order decides the winner and
           a static transparent silently beat the conditional colour. bg-transparent is
           likewise explicit, because tailwind.css deliberately skips Preflight (Pretzel
           supplies the reset) and Pretzel does not neutralise native button chrome — an
           unstyled button here renders in the browser's grey ButtonFace, which is what
           made the rail look washed out. Arbitrary colours are written
           [color:var(--x)] because bare [var(--x)] is ambiguous with width/size in
           Tailwind v4. -->
          @for (item of railItems(); track item.id) {
            <button
              type="button"
              role="tab"
              [id]="'dev-tab-' + item.id"
              [attr.aria-selected]="shell.active() === item.id"
              [attr.aria-controls]="'dev-panel-' + item.id"
              [tabindex]="shell.active() === item.id ? 0 : -1"
              class="flex items-center justify-between gap-08 rounded-medium border bg-transparent px-12 py-08 text-left text-[color:var(--p-color-text-primary)] hover:bg-[color:var(--p-color-background-hover)] hover:text-[color:var(--p-color-text-primary-hover)] active:bg-[color:var(--p-color-background-pressed)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--p-color-stroke-link-primary)]"
              [class.border-[color:var(--p-color-stroke-strong)]]="shell.active() === item.id"
              [class.border-transparent]="shell.active() !== item.id"
              (click)="shell.select(item.id)"
              (keydown)="onRailKeydown($event)"
            >
              <span class="flex items-center gap-08 truncate">
                <stb-icon [name]="item.icon" />
                {{ item.label }}
              </span>
              @if (item.showCount) {
                <span class="font-details shrink-0">{{ item.matchCount }}</span>
              }
            </button>
          }
        </div>

        <div class="shrink-0 border-t border-[var(--p-color-stroke-subtle-2)] p-12">
          @if (
            offlineMode ||
            (keycloakService.forcedAdvisorMode$ | async) ||
            !dispatchLogging ||
            (errorsService.hasCriticalErrors$ | async)
          ) {
            <div class="flex flex-wrap gap-08 pb-08">
              @if (offlineMode) {
                <stb-badge [variant]="'warning'" [size]="'small'" [title]="'OFFLINE'"></stb-badge>
              }
              @if (keycloakService.forcedAdvisorMode$ | async) {
                <stb-badge [variant]="'info'" [size]="'small'" [title]="'ADVISOR'"></stb-badge>
              }
              @if (!dispatchLogging) {
                <stb-badge [variant]="'neutral'" [size]="'small'" [title]="'DISPATCH LOGGING OFF'"></stb-badge>
              }
              @if (errorsService.hasCriticalErrors$ | async) {
                <stb-badge
                  [variant]="'error'"
                  [size]="'small'"
                  [title]="(errorsService.criticalErrors$ | async)!.length + ' CRITICAL ERRORS'"
                ></stb-badge>
              }
            </div>
          }

          <div
            data-testid="dev-identity"
            class="flex flex-col gap-08 font-body text-sm"
            *appLet="{ nin: nin$ | async, cmid: cmid$ | async } as ids"
          >
            <div class="flex items-center gap-04">
              <span class="flex flex-1 items-center gap-04 truncate">
                <stb-icon name="key" />
                Copy token
              </span>
              <button
                (click)="copyToken()"
                stbButtonCircle="secondary"
                size="small"
                iconName="copy"
                label="Copy token"
                data-testid="dev-identity-copy-token"
                [disabled]="offlineMode"
              ></button>
            </div>
            <div class="flex items-center gap-04">
              <span class="flex-1 truncate">NIN: {{ ids.nin ?? "—" }}</span>
              <button
                (click)="copyNin()"
                stbButtonCircle="secondary"
                size="small"
                iconName="copy"
                label="Copy NIN"
                data-testid="dev-identity-copy-nin"
                [disabled]="ids.nin === null || ids.nin === undefined"
              ></button>
            </div>
            <div class="flex items-center gap-04">
              <span class="flex-1 truncate">CmId: {{ ids.cmid ?? "—" }}</span>
              <button
                (click)="copyCMID()"
                stbButtonCircle="secondary"
                size="small"
                iconName="copy"
                label="Copy customer id"
                data-testid="dev-identity-copy-cmid"
                [disabled]="ids.cmid === null || ids.cmid === undefined"
              ></button>
            </div>
          </div>
        </div>
      </nav>

      <div data-testid="dev-content-pane" class="flex min-h-0 grow flex-col gap-24 overflow-y-auto p-16">
        <div
          role="tabpanel"
          [id]="'dev-panel-page'"
          [attr.aria-labelledby]="'dev-tab-page'"
          [hidden]="shell.active() !== 'page'"
          tabindex="0"
        >
          <!-- The mock rows below are the *only* copy of those controls: rendering a row twice
           would instantiate a second DevRowDirective, double-counting "N / M" and giving two
           rows the same label. Route-pinned rows that are not part of this workflow therefore
           stay in their home section rather than being mirrored here. -->
          <section
            devSection
            devSectionId="page"
            data-section="page"
            #pageSection="devSection"
            class="flex flex-col gap-16"
          >
            <h3 class="font-body-demibold flex items-center gap-04">
              <stb-icon name="compass" />
              On this page — {{ currentPageLabel() }}
            </h3>

            <div class="flex flex-col gap-08">
              <p class="font-body-demibold">Jump to page</p>
              <div class="flex flex-wrap gap-08">
                @for (link of pageLinks(); track link.path) {
                  <button
                    type="button"
                    [size]="'small'"
                    [rebrand]="true"
                    [stbButton]="'secondary'"
                    [disabled]="link.current"
                    [attr.aria-current]="link.current ? 'page' : null"
                    (click)="navigateToPage(link.path)"
                  >
                    {{ link.label }}
                  </button>
                }
              </div>
            </div>

            <div class="flex flex-col gap-08 rounded-medium border border-[var(--p-color-stroke-subtle-2)] p-12">
              <p class="font-body-demibold">Mock data</p>
              <!-- Ordered top to bottom: turn offline mode on, pick a scenario, apply it.
               Deliberately unnumbered — a sunk row hides itself but a numeral beside it
               would not, leaving an orphan digit in the card. -->

              @if (offlineModeToggleAllowed) {
                <dev-row
                  devRow="Offline mode"
                  devRowType="toggle"
                  icon="plug"
                  [devRowKeywords]="['mock data', 'no auth', 'reload']"
                >
                  <stb-switch
                    class="dev-switch-clipped-label"
                    [switchControl]="offlineModeControl"
                    [rebrand]="true"
                    [fluid]="true"
                    label="Offline mode"
                  ></stb-switch>
                </dev-row>
              }

              <dev-row
                devRow="Mock scenario"
                devRowType="state"
                icon="flask"
                devRowAvailability="requiresOffline"
                [devRowRoutes]="['avtaler', '/']"
                [devRowKeywords]="['preset', 'contracts', 'scenario', 'fixtures', 'empty state']"
              >
                <stb-form [rebrand]="true" size="fluid" class="w-full min-w-0">
                  <select
                    #presetSelect
                    data-testid="state-select-mock-scenario"
                    [disabled]="!offlineMode"
                    (change)="onPresetChange(presetSelect.value)"
                  >
                    @for (presetId of presetIds; track presetId) {
                      <option [value]="presetId" [selected]="presetId === selectedPresetId()">
                        {{ presetLabels[presetId] }}
                      </option>
                    }
                  </select>
                </stb-form>
              </dev-row>

              <dev-row
                devRow="Apply mock scenario"
                devRowType="action"
                icon="play"
                devRowAvailability="requiresOffline"
                [devRowRoutes]="['avtaler', '/']"
                description="IPS follows contract selection"
                [devRowKeywords]="['apply', 'reload', 'scenario']"
              >
                <button
                  [size]="'small'"
                  [rebrand]="true"
                  [stbButton]="'primary'"
                  [disabled]="!offlineMode"
                  (click)="applyMockScenario()"
                >
                  Apply & reload
                </button>
              </dev-row>

              @for (group of pageSection.sunkGroups(); track group.reason) {
                <button
                  type="button"
                  class="font-details flex w-full items-center justify-between border-t border-dashed border-[var(--p-color-stroke-subtle-2)] bg-transparent px-08 py-08"
                  (click)="pageSection.toggleFooter()"
                >
                  <span class="flex items-center gap-04">
                    <stb-icon [name]="group.tier === 'blocked' ? 'lock' : 'warning'" />
                    {{ group.rows.length }} {{ group.reason }}
                  </span>
                  <span>{{ pageSection.footerExpanded() ? "Hide" : "Show" }}</span>
                </button>
              }
            </div>

            <!-- Not page-scoped like the rest of this tab, but it is the control developers
             reach for most often, so it earns a place on the tab the panel opens to. These
             rows are the only copy — rendering them here and in Settings would instantiate a
             second DevRowDirective per repo and double-count the "N / M" row counter. -->
            <div class="flex flex-col gap-08 rounded-medium border border-[var(--p-color-stroke-subtle-2)] p-12">
              <p class="font-body-demibold">Feature environment</p>

              @for (entry of featureEnv.repos; track entry.repo) {
                <dev-row
                  [devRow]="entry.label"
                  devRowType="state"
                  [icon]="entry.icon"
                  [description]="featureEnv.activeBranch(entry.repo) ?? 'Not set'"
                  [devRowKeywords]="['feature', 'environment', 'branch', 'pod', entry.repo]"
                >
                  <div class="flex items-center gap-08">
                    <button
                      [size]="'small'"
                      [rebrand]="true"
                      [stbButton]="'primary'"
                      (click)="featureEnv.apply(entry.repo)"
                    >
                      Apply
                    </button>
                    <button
                      [size]="'small'"
                      [rebrand]="true"
                      [stbButton]="'secondary'"
                      [disabled]="!featureEnv.activeBranch(entry.repo)"
                      (click)="featureEnv.clear(entry.repo)"
                    >
                      Clear
                    </button>
                  </div>

                  <!-- Full-width body rather than the 250px control column: a branch name is
                   long, and squeezed in beside the buttons the field showed about six
                   characters. -->
                  <div devRowBody class="flex flex-col gap-08">
                    <!-- Label is clipped, not omitted: dev-row already renders it visibly, but
                     that is a plain <div>, so the input still needs its own accessible name. -->
                    <stb-form
                      [rebrand]="true"
                      [label]="entry.label"
                      size="fluid"
                      class="dev-form-clipped-label w-full min-w-0"
                    >
                      <input
                        type="text"
                        [attr.list]="'feature-env-recents-' + entry.repo"
                        [attr.data-testid]="'feature-env-input-' + entry.repo"
                        placeholder="branch name or GitHub .../tree/<branch> URL"
                        [value]="featureEnv.input(entry.repo)"
                        (input)="featureEnv.setInput(entry.repo, $any($event.target).value)"
                        (keyup.enter)="featureEnv.apply(entry.repo)"
                      />
                    </stb-form>
                    <datalist [id]="'feature-env-recents-' + entry.repo">
                      @for (branch of featureEnv.recentsFor(entry.repo); track branch) {
                        <option [value]="branch"></option>
                      }
                    </datalist>

                    @if (featureEnv.message(entry.repo); as message) {
                      <p
                        class="font-details text-[var(--p-color-text-negative)]"
                        aria-live="polite"
                        [attr.data-testid]="'feature-env-message-' + entry.repo"
                      >
                        {{ message.text }}
                      </p>
                    }
                  </div>
                </dev-row>
              }
            </div>
          </section>
          <!-- Stated rather than left blank: with tabs no longer disabled a filter can
           land the user on a section with nothing in it, and an empty pane reads as a
           rendering bug. Only while a query is active - an unfiltered section is empty
           because of where you are, not because of what you typed. -->
          @if (filter.normalizedQuery() !== "" && pageSection.matchingRowCount() === 0) {
            <p class="font-body text-[var(--p-color-text-secondary)]">No matches in this section.</p>
          }
        </div>

        <div
          role="tabpanel"
          [id]="'dev-panel-settings'"
          [attr.aria-labelledby]="'dev-tab-settings'"
          [hidden]="shell.active() !== 'settings'"
          tabindex="0"
        >
          <section devSection devSectionId="settings" #settingsSection="devSection" class="flex flex-col gap-16">
            <h3 class="font-body-demibold">Settings</h3>

            <!-- Offline mode lives in the "Mock data" card on the "This page" tab, next to the
             scenario select and the apply button it gates. -->
            <dev-row
              devRow="Advisor"
              devRowType="toggle"
              icon="user"
              description="Force advisor context"
              [devRowKeywords]="['advisor mode', 'incognito']"
            >
              <stb-switch
                class="dev-switch-clipped-label"
                [switchControl]="advisorModeControl"
                [rebrand]="true"
                [fluid]="true"
                label="Advisor"
              ></stb-switch>
            </dev-row>
            <dev-row
              devRow="Dispatch Logging"
              devRowType="toggle"
              icon="bug"
              description="Log dispatched actions"
              [devRowKeywords]="['dispatcher', 'logging']"
            >
              <stb-switch
                class="dev-switch-clipped-label"
                [switchControl]="dispatchLoggingControl"
                [rebrand]="true"
                [fluid]="true"
                label="Dispatch Logging"
              ></stb-switch>
            </dev-row>
            <div class="flex flex-col gap-08">
              <dev-row
                devRow="Feature toggles"
                devRowType="display"
                icon="list"
                [devRowKeywords]="['feature toggle', 'override']"
              >
                <button
                  type="button"
                  [size]="'small'"
                  [rebrand]="true"
                  [stbButton]="'secondary'"
                  [attr.aria-expanded]="featureTogglesOpen()"
                  [attr.aria-label]="featureTogglesOpen() ? 'Collapse feature toggles' : 'Expand feature toggles'"
                  (click)="featureTogglesOpen.set(!featureTogglesOpen())"
                >
                  <stb-icon [name]="featureTogglesOpen() ? 'caret-down' : 'caret-right'" />
                </button>
              </dev-row>

              @if (featureTogglesOpen() || filter.normalizedQuery() !== "") {
                <ng-container *appLet="featureToggles$ | async | keyvalue as featureToggles">
                  <div devSection class="flex flex-col gap-08 rounded-medium bg-[var(--p-color-background-grey)] p-12">
                    @for (featureToggle of featureToggles; track featureToggle.key) {
                      <dev-row
                        [devRow]="featureToggle.key"
                        devRowType="toggle"
                        class="cursor-pointer"
                        (click)="toggleFeatureOverride(featureToggle.key)"
                      >
                        <stb-checkbox [fluid]="true">
                          <input type="checkbox" [checked]="featureToggle.value === true" />
                          <label>{{ featureToggle.key }}</label>
                        </stb-checkbox>
                      </dev-row>
                    }
                  </div>
                </ng-container>
              }
            </div>

            <!-- State rows merged into Settings section.
             Mock scenario lives in the "Mock data" card on the "This page" tab. -->
            <dev-row
              devRow="smartAccount state"
              devRowType="state"
              icon="piggy-bank"
              devRowAvailability="mockedOffline"
              [devRowRoutes]="['/', 'smartkonto']"
              [devRowKeywords]="['smart account', 'override']"
            >
              <stb-form [rebrand]="true" size="fluid" class="w-full min-w-0">
                <select
                  data-testid="state-select-smartaccount"
                  #smartAccountSelect
                  (change)="smartAccountStateSelect(smartAccountSelect.value)"
                >
                  <option value="">No override</option>
                  @for (stateKey of smartAccountStateKeysList; track stateKey) {
                    <option
                      [value]="stateKey"
                      [selected]="stateKey === SmartAccountState[(smartAccountService.smartAccountState$ | async)!]"
                    >
                      {{ stateKey }}
                    </option>
                  }
                </select>
              </stb-form>
            </dev-row>

            <dev-row
              devRow="extrapension state"
              devRowType="state"
              icon="chart-line-up"
              devRowAvailability="mockedOffline"
              [devRowRoutes]="['/', 'avtaler']"
              [devRowKeywords]="['extra pension', 'override']"
            >
              <stb-form [rebrand]="true" size="fluid" class="w-full min-w-0">
                <select
                  data-testid="state-select-extrapension"
                  #extrapensionSelect
                  (change)="extrapensionStateSelect(extrapensionSelect.value)"
                >
                  <option value="">No override</option>
                  @for (stateKey of extrapensionStateKeysList; track stateKey) {
                    <option
                      [value]="stateKey"
                      [selected]="stateKey === ExtrapensionState[(extrapensionService.extrapensionState$ | async)!]"
                    >
                      {{ stateKey }}
                    </option>
                  }
                </select>
              </stb-form>
            </dev-row>

            <dev-row
              devRow="ips state"
              devRowType="state"
              icon="vault"
              devRowAvailability="mockedOffline"
              [devRowRoutes]="['/', 'avtaler']"
              [devRowKeywords]="['individual pension savings', 'override']"
            >
              <stb-form [rebrand]="true" size="fluid" class="w-full min-w-0">
                <select data-testid="state-select-ips" #ipsSelect (change)="ipsStateSelect(ipsSelect.value)">
                  <option value="">No override</option>
                  @for (stateKey of ipsStateValuesList; track stateKey) {
                    <option [value]="stateKey" [selected]="stateKey === (ipsStateValue$ | async)">
                      {{ stateKey }}
                    </option>
                  }
                </select>
              </stb-form>
            </dev-row>

            <dev-row
              devRow="AFP"
              devRowType="state"
              icon="certificate"
              [devRowRoutes]="['/', 'pensjonsplan']"
              [devRowKeywords]="['afp', 'avtalefestet pensjon', 'pension override']"
            >
              <stb-form [rebrand]="true" size="fluid" class="w-full min-w-0">
                <select data-testid="state-select-afp" #afpSelect (change)="onAfpChange(afpSelect.value)">
                  <option value="">No override</option>
                  <option value="true" [selected]="(afpValue$ | async) === 'true'">Yes</option>
                  <option value="false" [selected]="(afpValue$ | async) === 'false'">No</option>
                </select>
              </stb-form>
            </dev-row>

            <dev-row
              devRow="Pension age"
              devRowType="state"
              icon="calendar"
              [devRowRoutes]="['/', 'pensjonsplan']"
              [devRowKeywords]="['pension age', 'payout age', 'pensjonsalder', 'uttaksalder']"
            >
              <stb-form [rebrand]="true" size="fluid" class="w-full min-w-0">
                <!-- [selected] per option, not [value] on the select: the options
                     come from @for, which has not run when [value] is applied. -->
                <select
                  data-testid="state-select-pension-age"
                  #pensionAgeSelect
                  (change)="onPensionAgeChange(pensionAgeSelect.value)"
                >
                  <option value="">No override</option>
                  @for (age of startPayoutAgeRange$ | async; track age) {
                    <option [value]="age" [selected]="age === (startPayoutAge$ | async)">{{ age }}</option>
                  }
                </select>
              </stb-form>
            </dev-row>

            @for (group of settingsSection.sunkGroups(); track group.reason) {
              <button
                type="button"
                class="font-details flex w-full items-center justify-between border-t border-dashed border-[var(--p-color-stroke-subtle-2)] bg-transparent px-08 py-08"
                (click)="settingsSection.toggleFooter()"
              >
                <span class="flex items-center gap-04">
                  <stb-icon [name]="group.tier === 'blocked' ? 'lock' : 'warning'" />
                  {{ group.rows.length }} {{ group.reason }}
                </span>
                <span>{{ settingsSection.footerExpanded() ? "Hide" : "Show" }}</span>
              </button>
            }
          </section>
          <!-- Stated rather than left blank: with tabs no longer disabled a filter can
           land the user on a section with nothing in it, and an empty pane reads as a
           rendering bug. Only while a query is active - an unfiltered section is empty
           because of where you are, not because of what you typed. -->
          @if (filter.normalizedQuery() !== "" && settingsSection.matchingRowCount() === 0) {
            <p class="font-body text-[var(--p-color-text-secondary)]">No matches in this section.</p>
          }
        </div>

        <div
          role="tabpanel"
          [id]="'dev-panel-actions'"
          [attr.aria-labelledby]="'dev-tab-actions'"
          [hidden]="shell.active() !== 'actions'"
          tabindex="0"
        >
          <section devSection devSectionId="actions" #actionsSection="devSection" class="flex flex-col gap-16">
            <h3 class="font-body-demibold">Actions</h3>

            <!-- Apply mock scenario lives in the "Mock data" card on the "This page" tab, directly
             under the scenario select it applies. -->
            <dev-row
              devRow="Copy environment link"
              devRowType="action"
              icon="link"
              description="Share the current feature environment with a tester"
              [devRowKeywords]="['share', 'link', 'feature', 'environment', 'tester']"
            >
              <button
                [size]="'small'"
                [rebrand]="true"
                [stbButton]="'secondary'"
                [disabled]="!featureEnv.hasActiveEnv()"
                (click)="featureEnv.copyShareLink()"
              >
                Copy link
              </button>
            </dev-row>

            <div class="flex flex-col gap-08">
              <dev-row
                devRow="Copy token"
                devRowType="action"
                icon="key"
                devRowAvailability="requiresOnline"
                [devRowKeywords]="['jwt', 'bearer', 'auth', 'kctok']"
              >
                <button
                  [size]="'small'"
                  [rebrand]="true"
                  [stbButton]="'secondary'"
                  [disabled]="offlineMode"
                  (click)="copyToken()"
                >
                  Copy
                </button>
              </dev-row>

              <dev-row
                devRow="Step Up Auth"
                devRowType="action"
                icon="fingerprint"
                devRowAvailability="requiresOnline"
                [devRowKeywords]="['stepup', 'mfa']"
              >
                <button
                  [size]="'small'"
                  [rebrand]="true"
                  [stbButton]="'secondary'"
                  [disabled]="offlineMode"
                  (click)="stepUpAuth()"
                >
                  Run
                </button>
              </dev-row>

              <dev-row
                devRow="Logout"
                devRowType="action"
                icon="sign-out"
                devRowAvailability="requiresOnline"
                [devRowKeywords]="['sign out']"
              >
                <button [size]="'small'" [rebrand]="true" [stbButton]="'secondary'" (click)="logout()">Run</button>
              </dev-row>

              <dev-row
                devRow="Set logout survey cookie"
                devRowType="action"
                icon="cookie"
                [devRowKeywords]="['hotjar', 'survey']"
              >
                <button [size]="'small'" [rebrand]="true" [stbButton]="'secondary'" (click)="setLogoutCookie()">
                  Set
                </button>
              </dev-row>

              <dev-row
                devRow="Remove Avtaler V2 modal cookie"
                devRowType="action"
                icon="browsers"
                [devRowRoutes]="['avtaler']"
                [devRowKeywords]="['contracts', 'v2', 'modal']"
              >
                <button
                  (click)="removeContractsV2ModalCookie()"
                  [size]="'small'"
                  [rebrand]="true"
                  [stbButton]="'secondary'"
                >
                  Remove
                </button>
              </dev-row>

              <dev-row
                devRow="Remove Campaign modal cookie"
                devRowType="action"
                icon="megaphone"
                [devRowRoutes]="['/']"
                [devRowKeywords]="['campaign', 'modal']"
              >
                <button
                  (click)="removeCampaignModalCookie()"
                  [size]="'small'"
                  [rebrand]="true"
                  [stbButton]="'secondary'"
                >
                  Remove
                </button>
              </dev-row>
              <dev-row
                devRow="Generate Error"
                devRowType="action"
                icon="bug"
                [devRowKeywords]="['datadog', 'error', 'monitoring']"
              >
                <button [size]="'small'" [rebrand]="true" [stbButton]="'secondary'" (click)="generateDummyError()">
                  Generate
                </button>
              </dev-row>
              @for (action of devFetchActions.actions; track action.id) {
                <dev-row [devRow]="action.label" devRowType="action" [devRowKeywords]="action.keywords">
                  <button [size]="'small'" [rebrand]="true" [stbButton]="'secondary'" (click)="action.run()">
                    Run
                  </button>
                </dev-row>
              }
            </div>

            @for (group of actionsSection.sunkGroups(); track group.reason) {
              <button
                type="button"
                class="font-details flex w-full items-center justify-between border-t border-dashed border-[var(--p-color-stroke-subtle-2)] bg-transparent px-08 py-08"
                (click)="actionsSection.toggleFooter()"
              >
                <span class="flex items-center gap-04">
                  <stb-icon [name]="group.tier === 'blocked' ? 'lock' : 'warning'" />
                  {{ group.rows.length }} {{ group.reason }}
                </span>
                <span>{{ actionsSection.footerExpanded() ? "Hide" : "Show" }}</span>
              </button>
            }
          </section>
          <!-- Stated rather than left blank: with tabs no longer disabled a filter can
           land the user on a section with nothing in it, and an empty pane reads as a
           rendering bug. Only while a query is active - an unfiltered section is empty
           because of where you are, not because of what you typed. -->
          @if (filter.normalizedQuery() !== "" && actionsSection.matchingRowCount() === 0) {
            <p class="font-body text-[var(--p-color-text-secondary)]">No matches in this section.</p>
          }
        </div>

        <div
          role="tabpanel"
          [id]="'dev-panel-goto'"
          [attr.aria-labelledby]="'dev-tab-goto'"
          [hidden]="shell.active() !== 'goto'"
          tabindex="0"
        >
          <section devSection devSectionId="goto" #goToSection="devSection" class="flex flex-col gap-16">
            <h3 class="font-body-demibold">Go to</h3>

            <div class="flex flex-col gap-08">
              @for (page of pages | keyvalue; track page.key) {
                <dev-row [devRow]="page.key" devRowType="nav">
                  <button
                    [size]="'small'"
                    [rebrand]="true"
                    [stbButton]="'secondary'"
                    (click)="navigateToPage(page.value)"
                  >
                    Go
                  </button>
                </dev-row>
              }
            </div>

            <p class="font-body-demibold">Pension-plan steps</p>
            <div class="flex flex-col gap-08">
              @for (route of pensionPlanRoutes$ | async; track route.key) {
                <!-- Deliberately untagged: pinning all 8 step rows into "On this page"
                 duplicated this whole list one screenful above itself, which is the
                 opposite of the request to move the pension-plan steps further down. -->
                <dev-row [devRow]="route.key" devRowType="nav">
                  <button
                    [size]="'small'"
                    [rebrand]="true"
                    [stbButton]="'secondary'"
                    (click)="goToPensionPlanPage(route.url)"
                  >
                    Go
                  </button>
                </dev-row>
              }
            </div>

            <div class="flex flex-col gap-08">
              <dev-row
                devRow="Go to Mine Penger"
                devRowType="nav"
                icon="wallet"
                [devRowRoutes]="['pensjonsplan']"
                [devRowKeywords]="['mine penger', 'full reload']"
              >
                <a href="../min-pensjon/pensjonsplan/mine-penger?displaymode=app">
                  <button [size]="'small'" [rebrand]="true" [stbButton]="'secondary'">Go</button>
                </a>
              </dev-row>
            </div>

            @for (group of goToSection.sunkGroups(); track group.reason) {
              <button
                type="button"
                class="font-details flex w-full items-center justify-between border-t border-dashed border-[var(--p-color-stroke-subtle-2)] bg-transparent px-08 py-08"
                (click)="goToSection.toggleFooter()"
              >
                <span class="flex items-center gap-04">
                  <stb-icon [name]="group.tier === 'blocked' ? 'lock' : 'warning'" />
                  {{ group.rows.length }} {{ group.reason }}
                </span>
                <span>{{ goToSection.footerExpanded() ? "Hide" : "Show" }}</span>
              </button>
            }
          </section>
          <!-- Stated rather than left blank: with tabs no longer disabled a filter can
           land the user on a section with nothing in it, and an empty pane reads as a
           rendering bug. Only while a query is active - an unfiltered section is empty
           because of where you are, not because of what you typed. -->
          @if (filter.normalizedQuery() !== "" && goToSection.matchingRowCount() === 0) {
            <p class="font-body text-[var(--p-color-text-secondary)]">No matches in this section.</p>
          }
        </div>

        <div
          role="tabpanel"
          [id]="'dev-panel-diagnostics'"
          [attr.aria-labelledby]="'dev-tab-diagnostics'"
          [hidden]="shell.active() !== 'diagnostics'"
          tabindex="0"
        >
          <section devSection devSectionId="diagnostics" #diagnosticsSection="devSection" class="flex flex-col gap-16">
            <h3 class="font-body-demibold">Diagnostics</h3>

            <dev-row
              devRow="GlobalSpinner"
              devRowType="display"
              icon="broadcast"
              [devRowKeywords]="['running jobs', 'spinner', 'diagnostics', 'runtime']"
            >
              <button
                [size]="'small'"
                [rebrand]="true"
                [stbButton]="'primary'"
                (click)="globalRunningJobsService.forceStop()"
              >
                pop
              </button>
              <pre devRowBody class="font-details whitespace-pre-wrap break-all">{{
                globalRunningJobsService.runningJobs$ | async | json
              }}</pre>
            </dev-row>

            <dev-row
              devRow="PrognosesSpinner"
              devRowType="display"
              icon="chart-line"
              [devRowKeywords]="['prognoses', 'spinner', 'diagnostics', 'runtime']"
            >
              <button
                [rebrand]="true"
                [stbButton]="'primary'"
                [size]="'small'"
                (click)="loadingStateService.forceReset()"
              >
                pop
              </button>
              <pre devRowBody class="font-details whitespace-pre-wrap break-all">{{
                prognosesSpinner$ | async | json
              }}</pre>
            </dev-row>
            @if (errorsService.hasCriticalErrors$ | async) {
              <dev-row
                devRow="Critical errors"
                devRowType="display"
                icon="warning"
                [devRowKeywords]="['errors', 'reload']"
              >
                <button [size]="'small'" [stbButton]="'primary'" [rebrand]="true" (click)="refreshClicked()">
                  {{ "errorspage.button.reload" | fms }}
                </button>
                <div devRowBody>
                  @for (e of errorsService.criticalErrors$ | async; track e) {
                    <stb-alert [surfaceVariant]="'surface-1'" [heading]="e.status.toString()" [rebrand]="true">
                      <p>{{ e.statusText }}</p>
                      <p>{{ e.message }}</p>
                    </stb-alert>
                  }
                </div>
              </dev-row>
            }
            @for (inspector of inspectors; track inspector.id) {
              <dev-row
                [devRow]="inspector.label"
                devRowType="display"
                [devRowKeywords]="inspector.keywords"
                [devRowAvailability]="inspector.availability"
              >
                @if (shell.active() === "diagnostics") {
                  <div devRowBody class="flex flex-col gap-08">
                    @switch (inspector.id) {
                      @case ("env") {
                        <div class="flex items-center gap-08">
                          <button
                            [size]="'small'"
                            [rebrand]="true"
                            [stbButton]="'secondary'"
                            (click)="copyBodyText(body)"
                          >
                            Copy JSON
                          </button>
                        </div>
                        <pre
                          #body
                          class="max-h-[240px] overflow-auto rounded-medium bg-[var(--p-color-background-grey)] p-12 font-details whitespace-pre-wrap break-all"
                          >{{ env | json }}</pre>
                      }
                      @case ("remoteConfig") {
                        <div class="flex items-center gap-08">
                          <button
                            [size]="'small'"
                            [rebrand]="true"
                            [stbButton]="'secondary'"
                            (click)="copyBodyText(body)"
                          >
                            Copy JSON
                          </button>
                        </div>
                        <pre
                          #body
                          class="max-h-[240px] overflow-auto rounded-medium bg-[var(--p-color-background-grey)] p-12 font-details whitespace-pre-wrap break-all"
                          >{{ remoteConfig$ | async | json }}</pre>
                      }
                      @case ("customer") {
                        <div class="flex items-center gap-08">
                          <button
                            [size]="'small'"
                            [rebrand]="true"
                            [stbButton]="'secondary'"
                            (click)="copyBodyText(body)"
                          >
                            Copy JSON
                          </button>
                        </div>
                        <pre
                          #body
                          class="max-h-[240px] overflow-auto rounded-medium bg-[var(--p-color-background-grey)] p-12 font-details whitespace-pre-wrap break-all"
                          >{{ customer$ | async | json }}</pre>
                      }
                      @case ("consents") {
                        <ng-container *appLet="'Ikke tatt stilling til' as freshConsent">
                          <div class="flex items-center gap-08">
                            <button
                              [size]="'small'"
                              [rebrand]="true"
                              [stbButton]="'secondary'"
                              (click)="copyBodyText(body)"
                            >
                              Copy
                            </button>
                            <button
                              [size]="'small'"
                              [rebrand]="true"
                              [stbButton]="'secondary'"
                              (click)="refreshInspector('consents')"
                            >
                              Refresh
                            </button>
                          </div>
                          <pre
                            #body
                            class="max-h-[240px] overflow-auto rounded-medium bg-[var(--p-color-background-grey)] p-12 font-details whitespace-pre-wrap break-all"
                          >
storageConsent: {{ (consentService.customerSuppliedDataConsent$ | async) ?? freshConsent }}
norskPensjonConsent: {{ (consentService.hasNorskPensjonConsent$ | async) ?? freshConsent }}
navConsentValue: {{ (customerService.navConsent$ | async) ?? freshConsent }}
ofaConsentValue: {{ (customerService.ofaConsent$ | async) ?? freshConsent }}
extractAllConsents(): {{ extractedConsents() | json }}</pre>
                        </ng-container>
                      }
                      @case ("simParams") {
                        <div class="flex items-center gap-08">
                          <button
                            [size]="'small'"
                            [rebrand]="true"
                            [stbButton]="'secondary'"
                            (click)="copyBodyText(body)"
                          >
                            Copy
                          </button>
                        </div>
                        <pre
                          #body
                          class="max-h-[240px] overflow-auto rounded-medium bg-[var(--p-color-background-grey)] p-12 font-details whitespace-pre-wrap break-all"
                        >
annualGrossIncome: {{ incomeService.annualGrossIncome$ | async }}
startPayoutAge: {{ startPayoutAge$ | async }}
startPayoutAgePartialWithdrawal: {{ startPayoutAgePartialWithdrawal$ | async }}
startPayoutAgeRange: {{ startPayoutAgeRange$ | async }}
partTimePercentage: {{ partTimePercentage$ | async }}
withdrawalPercentage: {{ withdrawalPercentage$ | async }}
postPensionPartTimeEnable: {{ commonParametersService.postPensionPartTimeEnable$ | async }}
postPensionPartTimeEndAge: {{ commonParametersService.postPensionPartTimeEndAge$ | async }}
postPensionPartTimePercent: {{ commonParametersService.postPensionPartTimePercent$ | async }}</pre>
                      }
                      @case ("profile") {
                        <div class="flex items-center gap-08">
                          <button
                            [size]="'small'"
                            [rebrand]="true"
                            [stbButton]="'secondary'"
                            (click)="copyBodyText(body)"
                          >
                            Copy JSON
                          </button>
                        </div>
                        <pre
                          #body
                          class="max-h-[240px] overflow-auto rounded-medium bg-[var(--p-color-background-grey)] p-12 font-details whitespace-pre-wrap break-all"
                          >{{ profile$ | async | json }}</pre>
                      }
                      @case ("clientData") {
                        <div class="flex items-center gap-08">
                          <button
                            [size]="'small'"
                            [rebrand]="true"
                            [stbButton]="'secondary'"
                            (click)="copyBodyText(body)"
                          >
                            Copy JSON
                          </button>
                        </div>
                        <pre
                          #body
                          class="max-h-[240px] overflow-auto rounded-medium bg-[var(--p-color-background-grey)] p-12 font-details whitespace-pre-wrap break-all"
                          >{{ clientData$ | async | json }}</pre>
                      }
                      @case ("keycloakToken") {
                        <!-- No body offline: parsedIdToken$ would resolve against an
                         uninitialised Keycloak client. The row's `requiresOnline`
                         rule already renders the reason, so repeating it here would
                         both duplicate the sentence on screen and fork the string
                         away from DEV_AVAILABILITY_RULES. -->
                        @if (!offlineMode) {
                          <div class="flex items-center gap-08">
                            <button
                              [size]="'small'"
                              [rebrand]="true"
                              [stbButton]="'secondary'"
                              (click)="copyBodyText(body)"
                            >
                              Copy JSON
                            </button>
                          </div>
                          <pre
                            #body
                            class="max-h-[240px] overflow-auto rounded-medium bg-[var(--p-color-background-grey)] p-12 font-details whitespace-pre-wrap break-all"
                            >{{ keycloakService.parsedIdToken$ | async | json }}</pre>
                        }
                      }
                      @case ("sessionStorage") {
                        <div class="flex items-center gap-08">
                          <button
                            [size]="'small'"
                            [rebrand]="true"
                            [stbButton]="'secondary'"
                            (click)="copyBodyText(body)"
                          >
                            Copy JSON
                          </button>
                          <button
                            [size]="'small'"
                            [rebrand]="true"
                            [stbButton]="'secondary'"
                            (click)="refreshInspector('sessionStorage')"
                          >
                            Refresh
                          </button>
                        </div>
                        <pre
                          #body
                          class="max-h-[240px] overflow-auto rounded-medium bg-[var(--p-color-background-grey)] p-12 font-details whitespace-pre-wrap break-all"
                          >{{ sessionStorageSnapshot() | json }}</pre>
                      }
                      @case ("lifeExpectancy") {
                        <div class="flex items-center gap-08">
                          <button
                            [size]="'small'"
                            [rebrand]="true"
                            [stbButton]="'secondary'"
                            (click)="copyBodyText(body)"
                          >
                            Copy JSON
                          </button>
                        </div>
                        <pre
                          #body
                          class="max-h-[240px] overflow-auto rounded-medium bg-[var(--p-color-background-grey)] p-12 font-details whitespace-pre-wrap break-all"
                          >{{ lifeExpectancyData$ | async | json }}</pre>
                      }
                      @case ("publicPensionData") {
                        <div class="flex items-center gap-08">
                          <button
                            [size]="'small'"
                            [rebrand]="true"
                            [stbButton]="'secondary'"
                            (click)="copyBodyText(body)"
                          >
                            Copy JSON
                          </button>
                        </div>
                        <pre
                          #body
                          class="max-h-[240px] overflow-auto rounded-medium bg-[var(--p-color-background-grey)] p-12 font-details whitespace-pre-wrap break-all"
                          >{{ publicPensionData$ | async | json }}</pre>
                      }
                      @default {
                        <p class="font-details">No inspector body registered for "{{ inspector.id }}"</p>
                      }
                    }
                  </div>
                }
              </dev-row>
            }

            @for (group of diagnosticsSection.sunkGroups(); track group.reason) {
              <button
                type="button"
                class="font-details flex w-full items-center justify-between border-t border-dashed border-[var(--p-color-stroke-subtle-2)] bg-transparent px-08 py-08"
                (click)="diagnosticsSection.toggleFooter()"
              >
                <span class="flex items-center gap-04">
                  <stb-icon [name]="group.tier === 'blocked' ? 'lock' : 'warning'" />
                  {{ group.rows.length }} {{ group.reason }}
                </span>
                <span>{{ diagnosticsSection.footerExpanded() ? "Hide" : "Show" }}</span>
              </button>
            }
          </section>
          <!-- Stated rather than left blank: with tabs no longer disabled a filter can
           land the user on a section with nothing in it, and an empty pane reads as a
           rendering bug. Only while a query is active - an unfiltered section is empty
           because of where you are, not because of what you typed. -->
          @if (filter.normalizedQuery() !== "" && diagnosticsSection.matchingRowCount() === 0) {
            <p class="font-body text-[var(--p-color-text-secondary)]">No matches in this section.</p>
          }
        </div>
      </div>
    </div>
  </div>
</stb-modal>
